import { useEffect, useRef, useState } from "react";

/**
 * The two doctor list endpoints return different shapes:
 *  - getDoctors / getUser:      { userId, firstName, lastName, imageName, ... }
 *  - getConsultedDoctors:       { userId, doctorName, imageName, ... }
 *
 * Normalizing both into one shape here means every other component
 * (list items, chat header) only ever has to deal with `name`.
 */
export function normalizeDoctor(raw) {
  if (!raw) return null;

  const name =
    raw.doctorName ??
    `${raw.firstName ?? ""} ${raw.lastName ?? ""}`.trim();

  return {
    userId: raw.userId,
    name: name || "Unknown",
    imageName: raw.imageName ?? null,
    raw, // keep the original around in case a caller needs a field we didn't lift
  };
}

export function normalizeDoctorList(list) {
  return (list ?? []).map(normalizeDoctor).filter(Boolean);
}

/**
 * Debounces a fast-changing value (e.g. search input) so consumers
 * (API calls, filters) only react after the user pauses typing.
 */
export function useDebouncedValue(value, delayMs = 300) {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);

  return debounced;
}

/**
 * Returns the localStorage-parsed patient object once, memoized for the
 * lifetime of the component (avoids parsing the same JSON twice per render
 * across multiple call sites).
 */
export function useLoggedInPatient() {
  const ref = useRef(undefined);
  if (ref.current === undefined) {
    const json = localStorage.getItem("patientData");
    ref.current = json ? JSON.parse(json) : null;
  }
  return ref.current;
}